/**
 * PIM Sessions management page per COMP-007 Story 6.
 * Admin sees all sessions; Technician sees own sessions.
 * Status filter tabs, revoke action, countdown timer for active sessions.
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ShieldCheck, Clock, ExternalLink, Eye } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/sc/status-badge";
import { EmptyState } from "@/components/sc/empty-state";
import { CountdownTimer } from "@/components/sc/countdown-timer";
import { DataTable, type Column, type SortState } from "@/components/sc/data-table";
import { GlassCard } from "@/components/sc/glass-card";
import { useRole } from "@/hooks/use-role";
import {
  usePimSessions,
  usePimSessionsMine,
  useRevokeSession,
  useDeactivateSession,
} from "@/hooks/use-pim-sessions";
import { ADMIN_PORTAL_LINKS, MSP_REALM, type PIMSession, type TechnicianTier } from "@/types";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Active", value: "Active" },
  { label: "Pending", value: "PendingApproval" },
  { label: "Expired", value: "Expired" },
  { label: "Revoked", value: "Revoked" },
] as const;

const ADMIN_COLUMNS: Column<PIMSession>[] = [
  {
    key: "id",
    header: "Session ID",
    render: (row) => (
      <span className="font-mono text-label text-sc-text-muted" title={row.id}>
        {row.id.slice(0, 8)}...
      </span>
    ),
  },
  {
    key: "technicianName",
    header: "Technician",
    sortable: true,
    render: (row) => (
      <span className="text-body-sm text-sc-text font-medium">
        {row.technicianName}
      </span>
    ),
  },
  {
    key: "customerName",
    header: "Customer",
    sortable: true,
    render: (row) => (
      <span className="text-body-sm text-sc-text">{row.customerName}</span>
    ),
  },
  {
    key: "groupName",
    header: "Group",
    render: (row) => (
      <span className="text-body-sm text-sc-text-muted truncate block max-w-[150px]" title={row.groupName}>
        {row.groupName}
      </span>
    ),
  },
  {
    key: "roles",
    header: "Roles",
    render: (row) => (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-badge font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        {row.roles.length}
      </span>
    ),
  },
  {
    key: "activationTier",
    header: "Tier",
    render: (row) =>
      row.activationTier ? (
        <StatusBadge status={row.activationTier} category="techTier" />
      ) : (
        <span className="text-label text-sc-text-muted">-</span>
      ),
  },
  {
    key: "status",
    header: "Status",
    sortable: true,
    render: (row) => <StatusBadge status={row.status} category="pim" />,
  },
  {
    key: "activatedAt",
    header: "Activated",
    sortable: true,
    render: (row) => (
      <span className="text-body-sm text-sc-text-muted whitespace-nowrap">
        {relativeTime(row.activatedAt)}
      </span>
    ),
  },
  {
    key: "expiresAt",
    header: "Expires",
    render: (row) =>
      row.status === "Active" ? (
        <CountdownTimer expiresAt={row.expiresAt} />
      ) : (
        <span className="text-body-sm text-sc-text-muted">
          {relativeTime(row.expiresAt)}
        </span>
      ),
  },
  {
    key: "durationHours",
    header: "Duration",
    render: (row) => (
      <span className="text-body-sm text-sc-text">{row.durationHours}h</span>
    ),
  },
  {
    key: "haloTicketId",
    header: "Ticket",
    render: (row) =>
      row.haloTicketId ? (
        <span className="font-mono text-body-sm text-sc-text">
          #{row.haloTicketId}
        </span>
      ) : (
        <span className="text-label text-sc-text-muted">—</span>
      ),
  },
];

export function PimSessionsPage() {
  const role = useRole();
  const isAdmin = role === "admin";

  return isAdmin ? <AdminPimSessions /> : <TechnicianPimSessions />;
}

function AdminPimSessions() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState("Active");
  const [sort, setSort] = useState<SortState>({
    column: "activatedAt",
    direction: "desc",
  });
  const [revokeTarget, setRevokeTarget] = useState<PIMSession | null>(null);

  const sessionsQuery = usePimSessions({
    page,
    pageSize,
    status: statusFilter || undefined,
  });
  const revokeMutation = useRevokeSession();

  const sessions = sessionsQuery.data?.items ?? [];
  const total = sessionsQuery.data?.total ?? 0;

  function handleRevoke(session: PIMSession) {
    revokeMutation.mutate(
      { sessionId: session.id, reason: "Admin revocation from portal" },
      {
        onSuccess: () => {
          toast.success(
            `Session for ${session.technicianName} at ${session.customerName} revoked`,
          );
          setRevokeTarget(null);
        },
        onError: () => {
          toast.error("Failed to revoke session. Try again.");
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      {/* Status tabs */}
      <div className="flex items-center gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => {
              setStatusFilter(tab.value);
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-btn text-body-sm font-medium transition-colors ${
              statusFilter === tab.value
                ? "bg-sc-primary text-white"
                : "text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover border border-sc-border"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Revoke confirmation dialog */}
      {revokeTarget && (
        <GlassCard className="border-sc-danger/50">
          <h3 className="text-h3 font-display text-sc-danger mb-2">
            Confirm Revocation
          </h3>
          <p className="text-body-sm text-sc-text mb-4">
            Revoke {revokeTarget.technicianName}&apos;s session for{" "}
            {revokeTarget.customerName}? This will immediately remove their
            privileged access.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleRevoke(revokeTarget)}
              disabled={revokeMutation.isPending}
              className="px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-danger text-white hover:bg-sc-danger/90 disabled:opacity-50 transition-colors"
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke Session"}
            </button>
            <button
              onClick={() => setRevokeTarget(null)}
              className="px-4 py-2 rounded-btn text-body-sm text-sc-text-muted hover:text-sc-text"
            >
              Cancel
            </button>
          </div>
        </GlassCard>
      )}

      {/* Sessions table */}
      <DataTable<PIMSession>
        columns={[
          ...ADMIN_COLUMNS,
          {
            key: "actions",
            header: "",
            render: (row) =>
              row.status === "Active" ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void navigate({
                        to: "/customers/$customerId/activate",
                        params: { customerId: row.customerTenantId },
                      });
                    }}
                    className="px-2 py-1 rounded-btn text-label font-medium text-sc-primary border border-sc-primary/30 hover:bg-sc-primary/10 transition-colors"
                    title="View active session"
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRevokeTarget(row);
                    }}
                    className="px-2 py-1 rounded-btn text-label font-medium text-sc-danger border border-sc-danger/30 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    Revoke
                  </button>
                </div>
              ) : null,
          },
        ]}
        data={sessions}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        sort={sort}
        onSortChange={setSort}
        isLoading={sessionsQuery.isLoading}
        rowKey={(row) => row.id}
        emptyState={
          <EmptyState
            icon={<ShieldCheck size={48} />}
            heading="No sessions found"
            description="Sessions appear here when technicians activate PIM access."
          />
        }
      />
    </div>
  );
}

/** Get admin portal links relevant to a session's activation tier.
 * Note: domain-dependent portals are excluded from this view since we don't
 * have customer detail here. Full portal list is on the activate-session page. */
function getPortalLinksForSession(
  session: PIMSession,
): { name: string; url: string }[] {
  const tier = session.activationTier as TechnicianTier | null;
  if (!tier || session.status !== "Active") return [];

  return ADMIN_PORTAL_LINKS
    .filter((portal) => portal.relevantTiers.includes(tier))
    .filter((portal) => !portal.requiresDomain)
    .map((portal) => ({
      name: portal.name,
      url: portal.urlTemplate
        .replace("{tenantId}", session.customerTenantId)
        .replace("{realm}", MSP_REALM),
    }));
}

function TechnicianPimSessions() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("Active");
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const deactivateMutation = useDeactivateSession();

  const sessionsQuery = usePimSessionsMine({
    status: statusFilter || undefined,
  });
  const sessions = sessionsQuery.data?.items ?? [];

  const TECH_COLUMNS: Column<PIMSession>[] = [
    {
      key: "customerName",
      header: "Customer",
      sortable: true,
      render: (row) => (
        <span className="text-body-sm text-sc-text font-medium">
          {row.customerName}
        </span>
      ),
    },
    {
      key: "activationTier",
      header: "Tier",
      render: (row) =>
        row.activationTier ? (
          <StatusBadge status={row.activationTier} category="techTier" />
        ) : (
          <span className="text-label text-sc-text-muted">-</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <StatusBadge status={row.status} category="pim" />,
    },
    {
      key: "roles",
      header: "Roles",
      render: (row) => (
        <span className="text-body-sm text-sc-text">{row.roles.length}</span>
      ),
    },
    {
      key: "activatedAt",
      header: "Activated",
      render: (row) => (
        <span className="text-body-sm text-sc-text-muted">
          {relativeTime(row.activatedAt)}
        </span>
      ),
    },
    {
      key: "expiresAt",
      header: "Expires",
      render: (row) =>
        row.status === "Active" ? (
          <CountdownTimer expiresAt={row.expiresAt} />
        ) : (
          <span className="text-body-sm text-sc-text-muted">
            {relativeTime(row.expiresAt)}
          </span>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.status === "Active" && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void navigate({
                    to: "/customers/$customerId/activate",
                    params: { customerId: row.customerTenantId },
                  });
                }}
                className="px-2 py-1 rounded-btn text-label font-medium text-sc-primary border border-sc-primary/30 hover:bg-sc-primary/10 transition-colors"
                title="View active session"
              >
                <Eye size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedSession(expandedSession === row.id ? null : row.id);
                }}
                className="px-2 py-1 rounded-btn text-label font-medium text-blue-600 border border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/20 transition-colors"
                title="Admin portals"
              >
                <ExternalLink size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deactivateMutation.mutate(row.id, {
                    onSuccess: () => toast.success("Session deactivated"),
                    onError: () => toast.error("Failed to deactivate"),
                  });
                }}
                disabled={deactivateMutation.isPending}
                className="px-2 py-1 rounded-btn text-label font-medium text-sc-text-muted border border-sc-border hover:bg-sc-surface-hover transition-colors"
              >
                Deactivate
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  // Find the expanded session object for portal links
  const activeExpanded = expandedSession
    ? sessions.find((s) => s.id === expandedSession)
    : null;
  const portalLinks = activeExpanded
    ? getPortalLinksForSession(activeExpanded)
    : [];

  return (
    <div className="space-y-4">
      {/* Status tabs */}
      <div className="flex items-center gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-3 py-1.5 rounded-btn text-body-sm font-medium transition-colors ${
              statusFilter === tab.value
                ? "bg-sc-primary text-white"
                : "text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover border border-sc-border"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Admin portal links panel */}
      {activeExpanded && portalLinks.length > 0 && (
        <GlassCard>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-body-sm font-medium text-sc-text">
              Admin Portals for {activeExpanded.customerName}
            </h3>
            <button
              onClick={() => setExpandedSession(null)}
              className="text-label text-sc-text-muted hover:text-sc-text"
            >
              Close
            </button>
          </div>
          <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {portalLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-btn text-body-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                <ExternalLink size={14} />
                {link.name}
              </a>
            ))}
          </div>
        </GlassCard>
      )}

      <DataTable<PIMSession>
        columns={TECH_COLUMNS}
        data={sessions}
        isLoading={sessionsQuery.isLoading}
        rowKey={(row) => row.id}
        emptyState={
          <EmptyState
            icon={
              <div className="flex items-center gap-1">
                <ShieldCheck size={36} />
                <Clock size={24} />
              </div>
            }
            heading="No sessions found"
            description="Sessions appear here when you activate PIM access."
          />
        }
      />
    </div>
  );
}
